import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const analysisAccountsSchema = z.array(z.object({
  accountId: z.string(), accountName: z.string().nullish(),
}));
const dealBorrowerSchema = z.object({
  borrowerId: z.string().nullish(), borrowerType: z.string().nullish(), borrowerName: z.string().nullish(),
});
const accountPartiesSchema = z.object({ all: z.array(z.object({
  partyId: z.string(), partyType: z.string(), partyName: z.string().nullish(), status: z.string(),
})) });

export function DealFinancialEntryLinks({ dealId }: { dealId: string }) {
  const entities = useQuery<unknown>({
    queryKey: [`/api/financial/deals/${dealId}/analysis-entities`],
  });
  const deal = useQuery<unknown>({
    queryKey: ['/api/crm/deals', dealId],
  });
  const parties = useQuery<unknown>({
    queryKey: [`/api/crm/deals/${dealId}/parties`],
  });

  if (entities.isError || deal.isError || parties.isError) {
    return <p>Financial entry links could not be loaded. Refresh this page to try again.</p>;
  }
  if (entities.isPending || deal.isPending || parties.isPending) {
    return <p>Loading financial entry links...</p>;
  }

  const analysisAccounts = analysisAccountsSchema.safeParse(entities.data);
  const borrower = dealBorrowerSchema.safeParse(deal.data);
  const accountParties = accountPartiesSchema.safeParse(parties.data);
  if (!analysisAccounts.success || !borrower.success || !accountParties.success) {
    return <p>Financial entry links could not be loaded. Refresh this page to try again.</p>;
  }

  const accounts = new Map<string, string>();
  for (const entity of analysisAccounts.data) {
    if (entity.accountId) accounts.set(entity.accountId, entity.accountName || 'Account');
  }
  for (const party of accountParties.data.all) {
    if (party.partyType === 'account' && party.status === 'active' && !accounts.has(party.partyId)) {
      accounts.set(party.partyId, party.partyName || 'Account');
    }
  }
  if (borrower.data.borrowerType === 'account' && borrower.data.borrowerId && !accounts.has(borrower.data.borrowerId)) {
    accounts.set(borrower.data.borrowerId, borrower.data.borrowerName || 'Borrower');
  }

  if (accounts.size === 0) {
    return <p>No account is linked to this deal for financial data entry. Add an account to the deal before entering its financial data.</p>;
  }
  return <ul>
    {Array.from(accounts, ([id, name]) => <li key={id}>
      <a className="text-primary hover:underline" href={`/accounts/${id}/financials`}>
        {name} financials
      </a>
    </li>)}
  </ul>;
}
